import React from 'react'
import { Button, Col, Row, Select } from 'antd'
import { Controller, useFormContext } from 'react-hook-form'
import { CampaignType } from '../../../../__generated__/graphql'
import { useCurrentUser } from '../../../../utils/CurrentUserContext'
import { DeleteOutlined } from '@ant-design/icons'

/**
 * SectionsField Component
 *
 * Displays a searchable multi-select dropdown for targeting sections.
 * Only shown for in-house campaigns (Display & Native) when the company
 * has targetingSections configured.
 *
 * Data source: user.company.targetingSections (array of strings)
 * Saves to: campaign.targeting.sections (array of selected section names)
 */

const SectionsField: React.FC = () => {
  const { user } = useCurrentUser()
  const { watch, setValue, control } = useFormContext()
  const campaignType = watch('type')

  // Get available sections from company configuration
  const targetingSections = user?.company?.targetingSections || []

  // Only show for in-house campaigns (Display & Native)
  const isInHouseCampaign = [
    CampaignType.InHouse,
    CampaignType.InHouseNativeDisplay,
  ].includes(campaignType)

  // Show field if campaign is in-house type and company has sections available
  const shouldShow = isInHouseCampaign && targetingSections.length > 0

  if (!shouldShow) {
    return null
  }

  // Watch the field value to enable/disable delete button
  const value = watch('targeting.sections')
  const checked = !!value && value.length > 0

  // Clear function for delete button
  const handleClear = () => {
    if (checked) {
      setValue('targeting.sections', [], { shouldDirty: true })
    }
  }

  return (
    <Row>
      <Col span={8}>
        <label className='flex items-center text-sm font-medium text-gray-700'>
          <Button
            type='link'
            icon={<DeleteOutlined />}
            onClick={handleClear}
            danger
            disabled={!checked}
          />
          Sections
        </label>
      </Col>
      <Col span={16}>
        <Controller
          name='targeting.sections'
          control={control}
          render={({ field }) => (
            <Select
              {...field}
              mode='multiple'
              showSearch
              placeholder='Select sections to target'
              options={targetingSections.map((section) => ({
                label: section,
                value: section,
              }))}
              className='w-full sm:max-w-xs'
              size='large'
              optionFilterProp='label'
            />
          )}
        />
      </Col>
    </Row>
  )
}

export default SectionsField
